import React, {useEffect, useState} from 'react';
import {Flex, message, Pagination, Table} from "antd";
import styles from "../../../Monitoring/monitoringTable.module.css";
import MyText from "../../../../components/UI/Text/MyText";
import IntlMessage from "../../../../components/IntlMessage/IntlMessage";
import {useWindowSize} from "@uidotdev/usehooks";
import {useDispatch, useSelector} from "react-redux";
import {useNavigate} from "react-router-dom";
import WaterService from "../../../../services/WaterService";
import dayjs from "dayjs";

const Monitoring = () => {
    const size = useWindowSize();
    const { user, isLoading } = useSelector(state => state.user);
    const dispatch = useDispatch();
    const navigate = useNavigate();
    const [waterData, setWaterData] = useState([]);
    const [loading, setLoading] = useState(false);
    const [pagination, setPagination] = useState({ current: 1, pageSize: 10, total: 0 });
    const [lastRowData, setLastRowData] = useState(null);

    useEffect(() => {
        if (!user) {
            navigate('/login', { replace: true });
        }
    }, [user, navigate]);

    useEffect(() => {
        fetchData(pagination.current, pagination.pageSize);
        const fetchLastRow = async () => {
            try {
                const data = await WaterService.getLastWaterData();
                setLastRowData(data);
            } catch (e) {
                setLastRowData(null);
            }
        };
        fetchLastRow();
        // eslint-disable-next-line
    }, []);

    const fetchData = async (page = 1, pageSize = 10) => {
        setLoading(true);
        try {
            const data = await WaterService.getWaterData(page - 1, pageSize);
            setWaterData(data.list);
            setPagination(prev => ({
                ...prev,
                current: page,
                pageSize,
                total: data.pages ? data.pages * pageSize : 0,
            }));
        } catch (error) {
            setWaterData([]);
            setPagination({ current: 1, pageSize: 10, total: 0 });
            message.error("Failed to fetch water data");
        } finally {
            setLoading(false);
        }
    };

    const columns = [
        {
            title: <IntlMessage id="monitoring.waterId" />,
            key: 'waterId',
            width: 130,
            render: (row) => row.waterId
        },
        {
            title: <IntlMessage id="monitoring.time" />,
            key: 'time',
            width: 200,
            render: (row) => row.time ? dayjs(row.time).format('DD.MM.YYYY HH:mm') : ''
        },
        {
            title: <IntlMessage id="monitoring.velocity1" />,
            key: 'velocity1',
            width: 130,
            render: (row) => row.velocity1
        },
        {
            title: <IntlMessage id="monitoring.flow1" />,
            key: 'flow1',
            width: 130,
            render: (row) => row.flow1
        },
        {
            title: <IntlMessage id="monitoring.depth1" />,
            key: 'depth1',
            width: 130,
            render: (row) => row.depth1
        },
        {
            title: <IntlMessage id="monitoring.temp1" />,
            key: 'temp1',
            width: 130,
            render: (row) => row.temp1
        },
        {
            title: <IntlMessage id="monitoring.velocity2" />,
            key: 'velocity2',
            width: 130,
            render: (row) => row.velocity2
        },
        {
            title: <IntlMessage id="monitoring.flow2" />,
            key: 'flow2',
            width: 130,
            render: (row) => row.flow2
        },
        {
            title: <IntlMessage id="monitoring.depth2" />,
            key: 'depth2',
            width: 130,
            render: (row) => row.depth2
        },
        {
            title: <IntlMessage id="monitoring.temp2" />,
            key: 'temp2',
            width: 130,
            render: (row) => row.temp2
        },
        {
            title: <IntlMessage id="monitoring.velocity3" />,
            key: 'velocity3',
            width: 130,
            render: (row) => row.velocity3
        },
        {
            title: <IntlMessage id="monitoring.flow3" />,
            key: 'flow3',
            width: 130,
            render: (row) => row.flow3
        },
        {
            title: <IntlMessage id="monitoring.depth3" />,
            key: 'depth3',
            width: 130,
            render: (row) => row.depth3
        },
        {
            title: <IntlMessage id="monitoring.temp3" />,
            key: 'temp3',
            width: 130,
            render: (row) => row.temp3
        },
        {
            title: <IntlMessage id="monitoring.velocity4" />,
            key: 'velocity4',
            width: 130,
            render: (row) => row.velocity4
        },
        {
            title: <IntlMessage id="monitoring.flow4" />,
            key: 'flow4',
            width: 130,
            render: (row) => row.flow4
        },
        {
            title: <IntlMessage id="monitoring.depth4" />,
            key: 'depth4',
            width: 130,
            render: (row) => row.depth4
        },
        {
            title: <IntlMessage id="monitoring.temp4" />,
            key: 'temp4',
            width: 130,
            render: (row) => row.temp4
        },
        {
            title: <IntlMessage id="monitoring.velocityAVG" />,
            key: 'velocityAVG',
            width: 150,
            render: (row) => row.velocityAVG
        },
        {
            title: <IntlMessage id="monitoring.flowAVG" />,
            key: 'flowAVG',
            width: 150,
            render: (row) => row.flowAVG
        },
    ];

    const handleTableChange = (pagination) => {
        fetchData(pagination.current, pagination.pageSize);
    };

    return (
        <Flex style={{ width: "100%", position: "relative", padding: "20px 20px 20px 20px" }} vertical align={"center"} justify={"center"}>
            <Flex style={{ width: '100%', maxWidth: 1440, position: 'relative', margin: '0 auto' }} gap={20} vertical align={'center'} justify={'flex-start'}>
                <Flex style={{ width: '100%', justifyContent: 'center'}}>
                    <Pagination
                        current={pagination.current}
                        pageSize={pagination.pageSize}
                        total={pagination.total}
                        showSizeChanger
                        pageSizeOptions={[10, 20, 50, 100]}
                        onChange={(page, pageSize) => fetchData(page, pageSize)}
                    />
                </Flex>
                <Flex style={{ width: '100%', overflowX: 'auto' }}>
                    <Table
                        className={styles.customTable}
                        columns={columns}
                        dataSource={waterData}
                        loading={loading}
                        rowKey="waterId"
                        pagination={false}
                        scroll={{ x: 1800, y: 800 }}
                        bordered
                        size="middle"
                        rowClassName={(_, index) => index % 2 === 0 ? styles.evenRow : styles.oddRow}
                    />
                </Flex>
                {lastRowData && (
                    <>
                        <Flex style={{ width: '100%', justifyContent: 'center', marginTop: 32 }}>
                            <MyText style={{ fontSize: '1.7rem', fontWeight: 700, color: '#0050b3' }}>
                                <IntlMessage id="monitoring.lastRowBlockTitle" />
                            </MyText>
                        </Flex>
                        <div className={styles.lastRowBlock}>
                            <Flex vertical gap={24} style={{ width: '100%' }}>
                                <Flex gap={32} style={{ width: '100%' }}>
                                    <Flex className={styles.cell} vertical>
                                        <div className={styles.cellTitle}><IntlMessage id="monitoring.meter1" /></div>
                                        <Flex className={styles.measureRow}><span className={styles.measureLabel}><IntlMessage id="monitoring.velocity" />:</span><span className={styles.measureValue}>{lastRowData.velocity1}</span><span className={styles.measureUnit}><IntlMessage id="monitoring.velocity.unit" /></span></Flex>
                                        <Flex className={styles.measureRow}><span className={styles.measureLabel}><IntlMessage id="monitoring.flow" />:</span><span className={styles.measureValue}>{lastRowData.flow1}</span><span className={styles.measureUnit}><IntlMessage id="monitoring.flow.unit" /></span></Flex>
                                        <Flex className={styles.measureRow}><span className={styles.measureLabel}><IntlMessage id="monitoring.depth" />:</span><span className={styles.measureValue}>{lastRowData.depth1}</span><span className={styles.measureUnit}><IntlMessage id="monitoring.depth.unit" /></span></Flex>
                                        <Flex className={styles.measureRow}><span className={styles.measureLabel}><IntlMessage id="monitoring.temp" />:</span><span className={styles.measureValue}>{lastRowData.temp1}</span><span className={styles.measureUnit}><IntlMessage id="monitoring.temp.unit" /></span></Flex>
                                    </Flex>
                                    <Flex className={styles.cell} vertical>
                                        <div className={styles.cellTitle}><IntlMessage id="monitoring.meter2" /></div>
                                        <Flex className={styles.measureRow}><span className={styles.measureLabel}><IntlMessage id="monitoring.velocity" />:</span><span className={styles.measureValue}>{lastRowData.velocity2}</span><span className={styles.measureUnit}><IntlMessage id="monitoring.velocity.unit" /></span></Flex>
                                        <Flex className={styles.measureRow}><span className={styles.measureLabel}><IntlMessage id="monitoring.flow" />:</span><span className={styles.measureValue}>{lastRowData.flow2}</span><span className={styles.measureUnit}><IntlMessage id="monitoring.flow.unit" /></span></Flex>
                                        <Flex className={styles.measureRow}><span className={styles.measureLabel}><IntlMessage id="monitoring.depth" />:</span><span className={styles.measureValue}>{lastRowData.depth2}</span><span className={styles.measureUnit}><IntlMessage id="monitoring.depth.unit" /></span></Flex>
                                        <Flex className={styles.measureRow}><span className={styles.measureLabel}><IntlMessage id="monitoring.temp" />:</span><span className={styles.measureValue}>{lastRowData.temp2}</span><span className={styles.measureUnit}><IntlMessage id="monitoring.temp.unit" /></span></Flex>
                                    </Flex>
                                </Flex>
                                <Flex gap={32} style={{ width: '100%' }}>
                                    <Flex className={styles.cell} vertical>
                                        <div className={styles.cellTitle}><IntlMessage id="monitoring.meter3" /></div>
                                        <Flex className={styles.measureRow}><span className={styles.measureLabel}><IntlMessage id="monitoring.velocity" />:</span><span className={styles.measureValue}>{lastRowData.velocity3}</span><span className={styles.measureUnit}><IntlMessage id="monitoring.velocity.unit" /></span></Flex>
                                        <Flex className={styles.measureRow}><span className={styles.measureLabel}><IntlMessage id="monitoring.flow" />:</span><span className={styles.measureValue}>{lastRowData.flow3}</span><span className={styles.measureUnit}><IntlMessage id="monitoring.flow.unit" /></span></Flex>
                                        <Flex className={styles.measureRow}><span className={styles.measureLabel}><IntlMessage id="monitoring.depth" />:</span><span className={styles.measureValue}>{lastRowData.depth3}</span><span className={styles.measureUnit}><IntlMessage id="monitoring.depth.unit" /></span></Flex>
                                        <Flex className={styles.measureRow}><span className={styles.measureLabel}><IntlMessage id="monitoring.temp" />:</span><span className={styles.measureValue}>{lastRowData.temp3}</span><span className={styles.measureUnit}><IntlMessage id="monitoring.temp.unit" /></span></Flex>
                                    </Flex>
                                    <Flex className={styles.cell} vertical>
                                        <div className={styles.cellTitle}><IntlMessage id="monitoring.meter4" /></div>
                                        <Flex className={styles.measureRow}><span className={styles.measureLabel}><IntlMessage id="monitoring.velocity" />:</span><span className={styles.measureValue}>{lastRowData.velocity4}</span><span className={styles.measureUnit}><IntlMessage id="monitoring.velocity.unit" /></span></Flex>
                                        <Flex className={styles.measureRow}><span className={styles.measureLabel}><IntlMessage id="monitoring.flow" />:</span><span className={styles.measureValue}>{lastRowData.flow4}</span><span className={styles.measureUnit}><IntlMessage id="monitoring.flow.unit" /></span></Flex>
                                        <Flex className={styles.measureRow}><span className={styles.measureLabel}><IntlMessage id="monitoring.depth" />:</span><span className={styles.measureValue}>{lastRowData.depth4}</span><span className={styles.measureUnit}><IntlMessage id="monitoring.depth.unit" /></span></Flex>
                                        <Flex className={styles.measureRow}><span className={styles.measureLabel}><IntlMessage id="monitoring.temp" />:</span><span className={styles.measureValue}>{lastRowData.temp4}</span><span className={styles.measureUnit}><IntlMessage id="monitoring.temp.unit" /></span></Flex>
                                    </Flex>
                                </Flex>
                            </Flex>
                            <div className={styles.avgRow}><b><IntlMessage id="monitoring.flowAVG" />:</b> {lastRowData.flowAVG} <span className={styles.measureUnit}><IntlMessage id="monitoring.flowAVG.unit" /></span></div>
                            <div className={styles.avgRow}><b><IntlMessage id="monitoring.velocityAVG" />:</b> {lastRowData.velocityAVG} <span className={styles.measureUnit}><IntlMessage id="monitoring.velocityAVG.unit" /></span></div>
                        </div>
                    </>
                )}
            </Flex>
        </Flex>
    );
};

export default Monitoring;